import { useState, useEffect } from 'react';
import { adminApi } from '../../../utils/api';
import { Badge, Spinner, Tabs, Pagination } from './index';

const SOURCE_COLORS = {
    input: 'bg-blue-500',
    loop: 'bg-gray-400',
    update: 'bg-amber-500',
};

const SOURCE_BADGE = {
    input: 'info',
    loop: 'default',
    update: 'warning',
};

const fmtBytes = (n) => {
    if (n == null) return '-';
    if (n >= 1_048_576) return `${(n / 1_048_576).toFixed(1)} MB`;
    if (n >= 1_024) return `${(n / 1_024).toFixed(1)} KB`;
    return `${n} B`;
};

const truncId = (id, len = 12) => {
    if (!id) return '-';
    return id.length > len ? id.slice(0, len) + '...' : id;
};

function JsonBlock({ label, data, defaultOpen = false }) {
    if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) return null;
    return (
        <details open={defaultOpen}>
            <summary className="text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-700">{label}</summary>
            <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap max-h-[400px] overflow-y-auto bg-gray-50 rounded-md p-2 mt-1">
                {JSON.stringify(data, null, 2)}
            </pre>
        </details>
    );
}

function CheckpointNode({ checkpoint, isSelected, onClick }) {
    const source = checkpoint.source || 'loop';
    const dotColor = SOURCE_COLORS[source] || SOURCE_COLORS.loop;
    const writes = checkpoint.metadata?.writes || {};
    const writeChannels = Object.keys(writes);

    return (
        <div className="relative pl-6 mb-3">
            <div className={`absolute left-0 top-3 w-[11px] h-[11px] rounded-full border-2 border-white ${dotColor} z-10`} />
            <div
                className={`rounded-lg border transition-colors cursor-pointer ${
                    isSelected
                        ? 'border-blue-400 bg-blue-50/30 shadow-sm ring-1 ring-blue-200'
                        : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={onClick}
            >
                <div className="px-3 py-2.5">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-gray-700">Step {checkpoint.step ?? '-'}</span>
                        <Badge variant={SOURCE_BADGE[source] || 'default'}>{source}</Badge>
                        <span className="text-[11px] font-mono text-gray-400 truncate">{truncId(checkpoint.checkpoint_id, 16)}</span>
                        {checkpoint.parent_checkpoint_id && (
                            <span className="text-[10px] text-gray-300 font-mono">← {truncId(checkpoint.parent_checkpoint_id, 10)}</span>
                        )}
                    </div>
                    {writeChannels.length > 0 && (
                        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                            {writeChannels.map((ch) => (
                                <span key={ch} className="text-[11px] font-mono text-orange-600 bg-orange-50 rounded px-1.5 py-0.5">{ch}</span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function TimelineTab({ threadId, onSelectCheckpoint, selectedCheckpointId }) {
    const [data, setData] = useState({ checkpoints: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [offset, setOffset] = useState(0);
    const limit = 100;

    useEffect(() => { setOffset(0); }, [threadId]);

    useEffect(() => {
        setLoading(true);
        adminApi.getCheckpointTimeline(threadId, { limit, offset })
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [threadId, offset]);

    if (loading) return <Spinner text="Loading timeline..." center />;
    if (data.checkpoints.length === 0) return <p className="text-sm text-gray-400 text-center py-8">No checkpoints</p>;

    return (
        <div className="flex flex-col h-full">
            <div className="mb-3 text-xs text-gray-400 shrink-0">{data.total} checkpoint{data.total !== 1 ? 's' : ''}</div>
            <div className="flex-1 overflow-y-auto pr-2">
                <div className="relative min-h-full">
                    {data.checkpoints.length > 1 && (
                        <div className="absolute left-[5px] top-3 bottom-0 w-px bg-gray-400" style={{ zIndex: 1 }} />
                    )}
                    {data.checkpoints.map((cp, idx) => (
                        <CheckpointNode
                            key={cp.checkpoint_id}
                            checkpoint={cp}
                            isSelected={selectedCheckpointId === cp.checkpoint_id}
                            onClick={() => onSelectCheckpoint({
                                type: 'checkpoint',
                                threadId,
                                checkpointId: cp.checkpoint_id,
                                checkpointNs: cp.checkpoint_ns || '',
                                label: `Step ${cp.step ?? '-'} — ${cp.source || 'loop'}`,
                            })}
                        />
                    ))}
                </div>
            </div>
            <Pagination total={data.total} limit={limit} offset={offset} onChange={setOffset} />
        </div>
    );
}

function WritesTab({ threadId, onSelectItem, selectedKey }) {
    const [data, setData] = useState({ writes: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [channelFilter, setChannelFilter] = useState('');
    const [checkpointFilter, setCheckpointFilter] = useState('');
    const [offset, setOffset] = useState(0);
    const limit = 100;

    useEffect(() => {
        setOffset(0);
        setChannelFilter('');
        setCheckpointFilter('');
    }, [threadId]);

    useEffect(() => {
        setLoading(true);
        adminApi.getCheckpointWrites(threadId, {
            channel: channelFilter || undefined,
            checkpoint_id: checkpointFilter || undefined,
            decode: true,
            limit,
            offset,
        })
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [threadId, channelFilter, checkpointFilter, offset]);

    const handleFilterKeyDown = (e) => {
        if (e.key === 'Enter') { setOffset(0); }
    };

    const makeKey = (w, i) => `w-${w.checkpoint_id}-${w.task_id}-${w.idx}-${i}`;

    return (
        <div>
            <div className="flex items-center gap-2 mb-3">
                <input
                    type="text"
                    value={channelFilter}
                    onChange={(e) => setChannelFilter(e.target.value)}
                    onKeyDown={handleFilterKeyDown}
                    placeholder="Channel filter..."
                    className="h-7 w-36 rounded-md border border-gray-200 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
                <input
                    type="text"
                    value={checkpointFilter}
                    onChange={(e) => setCheckpointFilter(e.target.value)}
                    onKeyDown={handleFilterKeyDown}
                    placeholder="Checkpoint ID..."
                    className="h-7 w-44 rounded-md border border-gray-200 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
                <span className="text-xs text-gray-400 ml-auto">{data.total} write{data.total !== 1 ? 's' : ''}</span>
            </div>

            {loading ? (
                <Spinner text="Loading writes..." center />
            ) : data.writes.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No writes</p>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Checkpoint</th>
                                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Task ID</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 w-12">Idx</th>
                                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Channel</th>
                                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Type</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Size</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.writes.map((w, i) => {
                                const key = makeKey(w, i);
                                const isSel = selectedKey === key;
                                return (
                                    <tr
                                        key={key}
                                        className={`cursor-pointer transition-colors ${
                                            isSel ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : 'hover:bg-gray-50'
                                        }`}
                                        onClick={() => onSelectItem({
                                            type: 'write',
                                            key,
                                            data: w,
                                            label: `Write: ${w.channel}`,
                                        })}
                                    >
                                        <td className="px-3 py-1.5 font-mono text-xs text-gray-600">{truncId(w.checkpoint_id, 12)}</td>
                                        <td className="px-3 py-1.5 font-mono text-xs text-gray-600">{truncId(w.task_id, 12)}</td>
                                        <td className="px-3 py-1.5 text-right text-xs text-gray-500">{w.idx}</td>
                                        <td className="px-3 py-1.5 font-mono text-xs text-orange-600">{w.channel}</td>
                                        <td className="px-3 py-1.5 text-xs text-gray-500">{w.type || '-'}</td>
                                        <td className="px-3 py-1.5 text-right text-xs text-gray-400 font-mono">{fmtBytes(w.blob_size)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="mt-3">
                <Pagination total={data.total} limit={limit} offset={offset} onChange={setOffset} />
            </div>
        </div>
    );
}

function BlobsTab({ threadId, onSelectItem, selectedKey }) {
    const [data, setData] = useState({ blobs: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [offset, setOffset] = useState(0);
    const limit = 100;

    useEffect(() => { setOffset(0); }, [threadId]);

    useEffect(() => {
        setLoading(true);
        adminApi.getCheckpointBlobs(threadId, { decode: true, limit, offset })
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [threadId, offset]);

    const makeKey = (b, i) => `b-${b.channel}-${b.version}-${i}`;

    if (loading) return <Spinner text="Loading blobs..." center />;
    if (data.blobs.length === 0) return <p className="text-sm text-gray-400 text-center py-8">No blobs</p>;

    return (
        <div>
            <div className="mb-3 text-xs text-gray-400">{data.total} blob{data.total !== 1 ? 's' : ''}</div>
            <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Channel</th>
                            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Version</th>
                            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Type</th>
                            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Namespace</th>
                            <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Size</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {data.blobs.map((b, i) => {
                            const key = makeKey(b, i);
                            const isSel = selectedKey === key;
                            return (
                                <tr
                                    key={key}
                                    className={`cursor-pointer transition-colors ${
                                        isSel ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : 'hover:bg-gray-50'
                                    }`}
                                    onClick={() => onSelectItem({
                                        type: 'blob',
                                        key,
                                        data: b,
                                        label: `Blob: ${b.channel}`,
                                    })}
                                >
                                    <td className="px-3 py-1.5 font-mono text-xs text-purple-600">{b.channel}</td>
                                    <td className="px-3 py-1.5 font-mono text-xs text-gray-600">{truncId(b.version, 12)}</td>
                                    <td className="px-3 py-1.5 text-xs text-gray-500">{b.type || '-'}</td>
                                    <td className="px-3 py-1.5 font-mono text-xs text-gray-400">{b.checkpoint_ns || '(default)'}</td>
                                    <td className="px-3 py-1.5 text-right text-xs text-gray-400 font-mono">{fmtBytes(b.blob_size)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="mt-3">
                <Pagination total={data.total} limit={limit} offset={offset} onChange={setOffset} />
            </div>
        </div>
    );
}

function DetailPanel({ detail, onClose }) {
    if (!detail) return null;

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 shrink-0">
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-900 truncate">{detail.label}</p>
                    {detail.type === 'checkpoint' && (
                        <p className="text-[10px] font-mono text-gray-400 truncate mt-0.5">{detail.checkpointId}</p>
                    )}
                </div>
                <button
                    onClick={onClose}
                    className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
                {detail.type === 'checkpoint' && <CheckpointDetailContent detail={detail} />}
                {detail.type === 'write' && <WriteDetailContent data={detail.data} />}
                {detail.type === 'blob' && <BlobDetailContent data={detail.data} />}
            </div>
        </div>
    );
}

function CheckpointDetailContent({ detail }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        adminApi.getCheckpointDetail(detail.threadId, detail.checkpointId, {
            checkpoint_ns: detail.checkpointNs || '',
        })
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [detail.threadId, detail.checkpointId, detail.checkpointNs]);

    if (loading) return <Spinner text="Loading..." center />;
    if (!data) return <p className="text-xs text-gray-400 text-center py-4">Failed to load</p>;

    return (
        <div className="space-y-4">
            <JsonBlock label="Metadata" data={data.metadata} defaultOpen />
            <JsonBlock label="Checkpoint" data={data.checkpoint} />

            {data.writes?.length > 0 && (
                <div>
                    <p className="text-xs font-medium text-gray-700 mb-2">Writes ({data.writes.length})</p>
                    <div className="space-y-2">
                        {data.writes.map((w, i) => (
                            <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 text-xs border-b border-gray-100">
                                    <span className="font-mono text-orange-600 font-medium">{w.channel}</span>
                                    <span className="text-gray-400">{w.type || '-'}</span>
                                    <span className="text-gray-400 font-mono ml-auto">{fmtBytes(w.blob_size)}</span>
                                </div>
                                {w.blob_content != null && (
                                    <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap max-h-[300px] overflow-y-auto p-3">
                                        {typeof w.blob_content === 'string' ? w.blob_content : JSON.stringify(w.blob_content, null, 2)}
                                    </pre>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {data.blobs?.length > 0 && (
                <div>
                    <p className="text-xs font-medium text-gray-700 mb-2">Blobs ({data.blobs.length})</p>
                    <div className="space-y-2">
                        {data.blobs.map((b, i) => (
                            <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 text-xs border-b border-gray-100">
                                    <span className="font-mono text-purple-600 font-medium">{b.channel}</span>
                                    <span className="text-gray-400">{b.type || '-'}</span>
                                    <span className="text-gray-400 font-mono ml-auto">{fmtBytes(b.blob_size)}</span>
                                </div>
                                {b.blob_content != null && (
                                    <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap max-h-[300px] overflow-y-auto p-3">
                                        {typeof b.blob_content === 'string' ? b.blob_content : JSON.stringify(b.blob_content, null, 2)}
                                    </pre>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function WriteDetailContent({ data }) {
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div><span className="text-gray-500">Checkpoint</span></div>
                <div className="font-mono text-gray-700 truncate">{data.checkpoint_id}</div>
                <div><span className="text-gray-500">Task ID</span></div>
                <div className="font-mono text-gray-700 truncate">{data.task_id}</div>
                <div><span className="text-gray-500">Index</span></div>
                <div className="text-gray-700">{data.idx}</div>
                <div><span className="text-gray-500">Channel</span></div>
                <div className="font-mono text-orange-600">{data.channel}</div>
                <div><span className="text-gray-500">Type</span></div>
                <div className="text-gray-700">{data.type || '-'}</div>
                <div><span className="text-gray-500">Size</span></div>
                <div className="font-mono text-gray-700">{fmtBytes(data.blob_size)}</div>
            </div>
            {data.blob_content != null ? (
                <div>
                    <p className="text-xs font-medium text-gray-700 mb-1.5">Content</p>
                    <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap max-h-[600px] overflow-y-auto bg-gray-50 rounded-lg p-3 border border-gray-200">
                        {typeof data.blob_content === 'string' ? data.blob_content : JSON.stringify(data.blob_content, null, 2)}
                    </pre>
                </div>
            ) : (
                <p className="text-xs text-gray-400 italic">No decoded content available</p>
            )}
        </div>
    );
}

function BlobDetailContent({ data }) {
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div><span className="text-gray-500">Channel</span></div>
                <div className="font-mono text-purple-600">{data.channel}</div>
                <div><span className="text-gray-500">Version</span></div>
                <div className="font-mono text-gray-700 truncate">{data.version}</div>
                <div><span className="text-gray-500">Type</span></div>
                <div className="text-gray-700">{data.type || '-'}</div>
                <div><span className="text-gray-500">Namespace</span></div>
                <div className="font-mono text-gray-700">{data.checkpoint_ns || '(default)'}</div>
                <div><span className="text-gray-500">Size</span></div>
                <div className="font-mono text-gray-700">{fmtBytes(data.blob_size)}</div>
            </div>
            {data.blob_content != null ? (
                <div>
                    <p className="text-xs font-medium text-gray-700 mb-1.5">Content</p>
                    <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap max-h-[600px] overflow-y-auto bg-gray-50 rounded-lg p-3 border border-gray-200">
                        {typeof data.blob_content === 'string' ? data.blob_content : JSON.stringify(data.blob_content, null, 2)}
                    </pre>
                </div>
            ) : (
                <p className="text-xs text-gray-400 italic">No decoded content available</p>
            )}
        </div>
    );
}

export default function CheckpointViewer({ threadId, showDetailPanel = true }) {
    const [activeTab, setActiveTab] = useState('timeline');
    const [detail, setDetail] = useState(null);

    const tabs = [
        { id: 'timeline', label: 'Timeline' },
        { id: 'writes', label: 'Writes' },
        { id: 'blobs', label: 'Blobs' },
    ];

    const selectedCheckpointId = detail?.type === 'checkpoint' ? detail.checkpointId : null;
    const selectedKey = detail?.key || null;

    return (
        <div className="flex h-full">
            <div className={`min-w-0 flex flex-col ${showDetailPanel && detail ? 'flex-1' : 'flex-1'}`}>
                <div className="mb-3 shrink-0">
                    <Tabs tabs={tabs} active={activeTab} onChange={(t) => { setActiveTab(t); setDetail(null); }} />
                </div>
                <div className="flex-1 overflow-hidden">
                    {activeTab === 'timeline' && (
                        <TimelineTab
                            threadId={threadId}
                            onSelectCheckpoint={setDetail}
                            selectedCheckpointId={selectedCheckpointId}
                        />
                    )}
                    {activeTab === 'writes' && (
                        <WritesTab
                            threadId={threadId}
                            onSelectItem={setDetail}
                            selectedKey={selectedKey}
                        />
                    )}
                    {activeTab === 'blobs' && (
                        <BlobsTab
                            threadId={threadId}
                            onSelectItem={setDetail}
                            selectedKey={selectedKey}
                        />
                    )}
                </div>
            </div>
            {showDetailPanel && detail && (
                <div className="w-[480px] shrink-0 border-l border-gray-200 bg-white">
                    <DetailPanel detail={detail} onClose={() => setDetail(null)} />
                </div>
            )}
        </div>
    );
}
